import { useState, useEffect, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Address } from "../../../interfaces/interfaces";
import { RootState } from "../../../redux/store";
import IconWrapper from "../../../components/IconWrapper/IconWrapper";
import sendPaymentSelection, { PaymentOption } from "./sendPaymentSelection";
import checkOrderIsAbleToCCPayment from "./checkOrderIsAbleToCCPayment";
import { Container, StyledButton, Wrapper } from "../../../components/styled/styled-components";
import { FreightOption } from "../../../interfaces/types";
import { setCurrentOrderShipAddress, updateFreightOption } from "../../../redux/actions/orders";
import LoadingSpinner from "../../../components/LoadingSpinner/LoadingSpinner";
import Icon from "../../../components/Icon/icon";
import BankTransferInfo from "../../../components/PaymentInfo/BankTransferInfo";
import { Context, IContext } from "../../../context/ContextApp";
import EmailTransferInfo from "../../../components/PaymentInfo/EmailTransferInfo";
import { subdomainTypes } from "../../../context/availableSubdomains";
import { paymentChargeInfo } from "./data";

const PaymentSelection = () => {
  const [paymentOption, setPaymentOption] = useState<PaymentOption | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const { appBranch }: IContext = useContext(Context);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentOrder = useSelector((state: RootState) => state.orders.currentOrder);

  const freightOption: FreightOption = useSelector(
    (state: RootState) => state.orders.freightOption,
  );

  const currentOrderShipAddress: Address = useSelector(
    (state: RootState) => state.orders.currentOrderShipAddress,
  );

  useEffect(() => {
    const CODVerification = async () => {
      const response = await checkOrderIsAbleToCCPayment();
      // if is not available
      if (!response.isAvailable) {
        // redirecting to checkout order
        navigate("/check-out/order-review");
      }

      // if is available
      setIsLoadingPage(false);
    };

    if (!currentOrder || currentOrder.order_items.length === 0) {
      navigate("/store/cart");
    } else {
      CODVerification();
    }
  }, [navigate, currentOrder]);

  useEffect(() => {
    dispatch(setCurrentOrderShipAddress(currentOrderShipAddress));
    dispatch(updateFreightOption(freightOption));
  }, [navigate, dispatch, currentOrderShipAddress, freightOption]);

  const handleOnContinue = async () => {
    setIsLoading(true);
    if (paymentOption) {
      const res = await sendPaymentSelection(paymentOption);
      if (res.status !== 201) {
        setIsLoading(false);
        return;
      }

      setIsLoading(false);
      dispatch(setCurrentOrderShipAddress(currentOrderShipAddress));
      dispatch(updateFreightOption(freightOption));
      return navigate("/check-out/order-review");
    }
  };

  const onlinePaymentIcon = (
    <IconWrapper
      text="Visa/Mastercard"
      isMultiChoice
      role="onlineSelection"
      isSelected={paymentOption === PaymentOption.payOnline}
      onClick={() => setPaymentOption(PaymentOption.payOnline)}
    >
      <Icon name="OnlinePaymentIcon" iconWidth="1.5rem" />
    </IconWrapper>
  );

  if (isLoadingPage) {
    return (
      <div style={{ margin: "10px" }}>
        <Wrapper>
          <LoadingSpinner />
        </Wrapper>
      </div>
    );
  }

  const chargeInfo = appBranch && paymentChargeInfo[appBranch];

  return (
    <div className="payment-selection-page">
      <h4>Payment method</h4>

      <Container row margin="1em 1em 1em 0">
        {/* ONLINE PAYMENT BLOCK */}
        <div style={{ marginRight: "1rem" }}>{onlinePaymentIcon}</div>

        {/* BANK TRANSFER BLOCK */}
        <IconWrapper
          text="Bank Transfer"
          isMultiChoice
          role="bankTransferSelection"
          isSelected={paymentOption === PaymentOption.BankTransfer}
          onClick={() => setPaymentOption(PaymentOption.BankTransfer)}
        >
          <Icon name="BankTransferIcon" iconWidth="1.5rem" />
        </IconWrapper>

        {appBranch === subdomainTypes.CA && (
          <div style={{ marginLeft: "1rem" }}>
            <IconWrapper
              text="Interac e-Transfer"
              isMultiChoice
              role="emailTransferSelection"
              isSelected={paymentOption === PaymentOption.EmailTransfer}
              onClick={() => setPaymentOption(PaymentOption.EmailTransfer)}
            >
              <Icon name="MailOpened" iconWidth="1.5rem" />
            </IconWrapper>
          </div>
        )}
      </Container>

      {paymentOption === PaymentOption.payOnline && chargeInfo && (
        <div className="paymentTableWrapper">
          <table className="paymentTable">
            <tbody>
              <tr>
                <th>Note: {chargeInfo.label}</th>
              </tr>
              {chargeInfo.details.length > 0 && (
                <tr>
                  <ul>
                    {chargeInfo.details.map((detail: string, index: number) => (
                      <li key={index}>{detail}</li>
                    ))}
                  </ul>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {paymentOption === PaymentOption.BankTransfer && <BankTransferInfo />}
      {paymentOption === PaymentOption.EmailTransfer && <EmailTransferInfo />}

      {paymentOption !== null && (
        <StyledButton
          role="continueButton"
          primary
          disabled={isLoading}
          onClick={() => !isLoading && handleOnContinue()}
        >
          {isLoading ? "Loading..." : "Continue"}
        </StyledButton>
      )}
    </div>
  );
};

export default PaymentSelection;
